/**
 * BI > Kardex — consulta analítica e resumo (#MT-1DB4950).
 * Endpoints: GET .../reports/kardex e .../reports/kardex/summary
 */
import api from '../api.js';
import db from '../../versa/db.js';
import { translate } from '../../versa/filter.js';
import { notify } from '../../versa/msgbox.js';
import pdf from '../../versa/pdf.js';
import { formatLotNumber } from '../../versa/misc.js';
import { formatTransactionEndpointDisplay } from '../utils/format-transaction-endpoint.js';
import * as types from '../types.js';

function $(sel, root = document) {
	return root.querySelector(sel);
}

function buildQuery(params) {
	const sp = new URLSearchParams();
	for (const [k, v] of Object.entries(params)) {
		if (v === undefined || v === null || v === '') continue;
		sp.set(k, String(v));
	}
	return sp.toString();
}

function esc(s) {
	if (s == null) return '';
	const t = document.createElement('template');
	t.textContent = String(s);
	return t.innerHTML;
}

/** SKU como string (API devolve array de segmentos). */
function skuLabel(p) {
	if (!p?.sku) return '';
	return Array.isArray(p.sku) ? p.sku.join('') : String(p.sku);
}

function filterExprCompany(companyId) {
	return translate({
		'field-0': 'company_id',
		'operator-0': 'eq',
		'value-0': companyId,
	});
}

async function loadCompanies(selectEl) {
	const res = await api.list_all_companies();
	if (!res.ok) {
		notify(await res.text(), 'Empresas');
		return;
	}
	let list;
	try {
		list = await res.json();
	} catch (e) {
		notify('Resposta de empresas inválida (JSON).', 'Empresas');
		return;
	}
	if (!Array.isArray(list)) {
		notify('Lista de empresas em formato inesperado.', 'Empresas');
		return;
	}
	selectEl.innerHTML = '<option value="">— Selecione —</option>';
	for (const c of list) {
		const o = document.createElement('option');
		o.value = c.id;
		o.textContent = c.nick || c.name || c.id;
		selectEl.appendChild(o);
	}
}

/**
 * Lista todos os produtos da empresa. A API limita `limit` a 500 (versa/utils/query.py),
 * então paginamos por offset. Incluímos inativos (`include_disabled=1`) para Kardex —
 * produto desabilitado ainda pode ter estoque/movimentos e mesmo SKU em outra empresa.
 */
async function loadProductsForCompany(companyId, selectEl, searchInput) {
	selectEl.innerHTML = '';
	if (!companyId) {
		selectEl.innerHTML = '<option value="">— Escolha a empresa —</option>';
		return;
	}
	const fe = filterExprCompany(companyId);
	const batch = 500;
	const products = [];
	const seen = new Set();
	for (let offset = 0; ; offset += batch) {
		const q = `?filter=${encodeURIComponent(fe)}&limit=${batch}&offset=${offset}&sort=7&include_disabled=1`;
		const res = await api.list_products(q);
		if (!res.ok) {
			notify(await res.text(), 'Produtos');
			selectEl.innerHTML = '<option value="">— Erro ao carregar —</option>';
			return;
		}
		const chunk = await res.json();
		if (!Array.isArray(chunk) || chunk.length === 0) {
			break;
		}
		for (const p of chunk) {
			const id = p?.id;
			if (id == null || seen.has(id)) {
				continue;
			}
			seen.add(id);
			products.push(p);
		}
		if (chunk.length < batch) {
			break;
		}
	}
	selectEl.innerHTML = '<option value="">— Selecione o produto (SKU / nome) —</option>';
	for (const p of products) {
		const sku = skuLabel(p);
		const name = (p.name || '').trim() || '(sem nome)';
		const o = document.createElement('option');
		o.value = String(p.id);
		const dis = p.is_disabled ? ' [inativo]' : '';
		o.textContent = `${sku} — ${name} (id ${p.id})${dis}`;
		o.dataset.sku = sku.toLowerCase();
		o.dataset.name = name.toLowerCase();
		selectEl.appendChild(o);
	}
	if (searchInput) {
		searchInput.value = '';
		applyProductSearch(selectEl, searchInput.value);
	}
}

function applyProductSearch(selectEl, needle) {
	const n = (needle || '').trim().toLowerCase();
	const opts = selectEl.querySelectorAll('option');
	/** Nunca ocultar a opção já selecionada: Chrome/WebKit podem zerar select.value se ela ficar hidden. */
	const selectedVal = selectEl.value || '';
	for (const o of opts) {
		if (!o.value) {
			o.hidden = false;
			continue;
		}
		if (selectedVal && o.value === selectedVal) {
			o.hidden = false;
			continue;
		}
		if (!n) {
			o.hidden = false;
			continue;
		}
		const sku = o.dataset.sku || '';
		const name = o.dataset.name || '';
		const txt = (o.textContent || '').toLowerCase();
		o.hidden = !(sku.includes(n) || name.includes(n) || txt.includes(n));
	}
}

const MOVEMENT_KIND_PT = {
	entrada_para_posicao: 'Entrada para posição',
	transferencia_entre_posicoes: 'Transferência entre posições',
	reserva_na_posicao: 'Reserva na posição',
	baixa_saida: 'Baixa (saída)',
	estorno_reserva: 'Estorno de reserva',
	unpick_ou_estorno_reserva_bloqueado: 'Unpick / estorno reserva (bloqueado)',
};

function movementKindLabel(kind) {
	if (kind == null || kind === '') return '—';
	return MOVEMENT_KIND_PT[kind] || String(kind).replace(/_/g, ' ');
}

async function kardexDbKey(store, keys, labels = 'name', separator = ' ')
{
	if (!keys || (Array.isArray(keys) && (keys.length === 0 || keys.some(v => v == null || v === ''))))
		return;
	const entry = await db.get(store, keys);
	if (!entry)
		return;
	return (Array.isArray(labels) ? labels : [labels]).map(label => entry[label]).filter(v => v != null && v !== '').join(separator);
}

/** Carrega caches locais para resolver nomes (armazém, posição, lote, entradas, saídas). */
async function prefetchKardexLookups(companyId, productId)
{
	if (!companyId)
		return;
	const q = (filter) => `?filter=${encodeURIComponent(filter)}&limit=5000`;
	const feCo = `company_id*eq*${companyId}`;
	const feLots = `${feCo},product_id*eq*${productId}`;
	const jobs = [
		[api.list_warehouses, 'warehouses', feCo],
		[api.list_locations, 'locations', feCo],
		[api.list_lots, 'lots', feLots],
		[api.list_inputs, 'inputs', feCo],
		[api.list_outputs, 'outputs', feCo],
	];
	for (const [listFn, store, filter] of jobs)
	{
		try
		{
			const data = await api.call(listFn, q(filter));
			if (Array.isArray(data) && data.length)
				await db.set(store, data);
		}
		catch (e)
		{
			console.warn('Kardex prefetch', store, e);
		}
	}
}

function transactionTypePairLabel(sourceType, targetType)
{
	const opt = types.opt_transaction_type().options;
	const a = (c) => opt.find(x => x.value === c)?.label || c || '';
	return `${a(sourceType)}→${a(targetType)}`;
}

async function formatKardexLotCell(companyId, lotId)
{
	if (lotId == null || lotId === '')
		return '—';
	try
	{
		const lot = await db.get('lots', [companyId, lotId]);
		if (lot?.lot_number != null && String(lot.lot_number).trim() !== '')
			return formatLotNumber(lot.lot_number);
	}
	catch (e)
	{
		console.warn(e);
	}
	return '—';
}

/** Data/hora para exibição (use com textContent; não retorna HTML). */
function formatKardexDateTimeForDisplay(iso) {
	if (iso == null || iso === '') return '—';
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return String(iso);
	return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'medium' });
}

function appendCell(tr, text, className, title) {
	const td = document.createElement('td');
	td.textContent = text == null ? '' : String(text);
	if (className) td.className = className;
	if (title != null && title !== '') td.title = String(title);
	tr.appendChild(td);
}

/** Quando não há linhas: explica filtro por empresa (comum confundir com a coluna Empresa em Transações). */
function kardexNoRowsHintHtml(data) {
	const cid = data?.company_id != null && data.company_id !== '' ? esc(String(data.company_id)) : '';
	const n =
		data?.total_rows_in_period ?? data?.line_count ?? 0;
	let html = '<p class="mts">Nenhuma linha no período (com os filtros atuais).</p>';
	if (cid) {
		html += `<p class="mts muted">Empresa da consulta: <code>${cid}</code></p>`;
	}
	if (n === 0) {
		html += `<p class="mts muted">O Kardex lista só movimentos em que <strong>transactions.company_id</strong> é exatamente essa empresa — o mesmo critério da coluna <strong>Empresa</strong> na tela <strong>Transações</strong>. Se as movimentações que você busca aparecem lá para outra empresa (outro UUID), selecione essa empresa aqui e o produto dela; o mesmo <code>product_id</code> em outro cadastro de empresa é outro estoque.</p>`;
	}
	return html;
}

async function renderDetail(container, data, costTransaction, companyId) {
	if (!data.rows?.length) {
		container.innerHTML = kardexNoRowsHintHtml(data);
		return;
	}

	container.replaceChildren();

	const summary = document.createElement('p');
	summary.className = 'mts muted';
	summary.innerHTML = `Saldo inicial: <strong>${esc(data.opening_qty)}</strong>${
		costTransaction ? ` | Valor inicial: <strong>${esc(data.opening_value ?? '—')}</strong>` : ''
	} | Linhas no período: <strong>${esc(data.total_rows_in_period)}</strong> (mostrando ${esc(data.rows.length)} após offset ${esc(data.offset ?? 0)})`;
	container.appendChild(summary);

	const scroll = document.createElement('div');
	scroll.className = 'kardex-scroll';
	const table = document.createElement('table');
	table.className = 'kardex-table';

	const thead = document.createElement('thead');
	const headTr = document.createElement('tr');
	const headLabels = [
		'Seq.', 'Data/Hora', 'Numeração lote', 'Movimento', 'Tipo', 'Origem', 'Destino',
		['Qtd', 'tar'], ['Δ qtd', 'tar'], ['Saldo qtd', 'tar'],
	];
	if (costTransaction) {
		headLabels.push(['Valor sinal', 'tar'], ['Saldo valor', 'tar']);
	}
	for (const h of headLabels) {
		const th = document.createElement('th');
		if (Array.isArray(h)) {
			th.textContent = h[0];
			th.className = h[1];
		} else {
			th.textContent = h;
		}
		headTr.appendChild(th);
	}
	thead.appendChild(headTr);
	table.appendChild(thead);

	const tbody = document.createElement('tbody');
	const co = companyId || data.company_id;
	const off = Number(data.offset) || 0;
	let lineIx = 0;
	for (const r of data.rows) {
		const tr = document.createElement('tr');
		const mk = r.movement_kind;
		const tid = r.transaction_id ?? r.id;
		lineIx += 1;
		appendCell(tr, String(off + lineIx), '', `Ordem no relatório (id interno da transação: ${tid})`);
		appendCell(tr, formatKardexDateTimeForDisplay(r.date_time), '');
		appendCell(tr, await formatKardexLotCell(co, r.lot_id), '');
		appendCell(tr, movementKindLabel(mk), '', mk);
		appendCell(tr, transactionTypePairLabel(r.source_type, r.target_type), '');
		appendCell(tr, await formatTransactionEndpointDisplay(kardexDbKey, co, r.source_type, r.source), 'kx-cell-ref');
		appendCell(tr, await formatTransactionEndpointDisplay(kardexDbKey, co, r.target_type, r.target), 'kx-cell-ref');
		appendCell(tr, r.quantity, 'tar');
		appendCell(tr, r.qty_delta, 'tar');
		appendCell(tr, r.balance_qty, 'tar');
		if (costTransaction) {
			appendCell(tr, r.value_signed, 'tar');
			appendCell(tr, r.balance_value, 'tar');
		}
		tbody.appendChild(tr);
	}
	table.appendChild(tbody);
	scroll.appendChild(table);
	container.appendChild(scroll);
}

/** Exibe célula de resumo (evita innerHTML + colisão de nomes no bundle). */
function summaryCellText(v) {
	if (v == null || v === '') return '—';
	return String(v);
}

function renderSummary(el, data, costTransaction) {
	el.replaceChildren();
	if (!data || typeof data !== 'object') {
		const p = document.createElement('p');
		p.className = 'mts muted';
		p.textContent = 'Não foi possível interpretar o resumo (resposta inválida).';
		el.appendChild(p);
		return;
	}

	const heading = document.createElement('h3');
	heading.className = 'kardex-summary-heading';
	heading.textContent = 'Resumo do período';

	const rows = [
		['Saldo inicial (qtd)', data.opening_qty],
		['Entradas no período (soma Δ+)', data.period_entries_qty],
		['Saídas no período (soma |Δ−|)', data.period_exits_qty],
		['Saldo líquido no período (qtd)', data.period_net_qty],
		['Saldo final (qtd)', data.closing_qty],
		['Linhas no período', data.line_count],
	];
	if (costTransaction) {
		rows.splice(1, 0, ['Valor inicial', data.opening_value ?? '—']);
		rows.push(['Valor líquido no período', data.period_net_value ?? '—']);
		rows.push(['Saldo final (valor)', data.closing_value ?? '—']);
	}

	const table = document.createElement('table');
	table.className = 'kardex-summary-table';
	const tbody = document.createElement('tbody');
	for (const [label, val] of rows) {
		const tr = document.createElement('tr');
		const th = document.createElement('th');
		th.textContent = label;
		const td = document.createElement('td');
		td.textContent = summaryCellText(val);
		td.className = 'tar';
		tr.appendChild(th);
		tr.appendChild(td);
		tbody.appendChild(tr);
	}
	table.appendChild(tbody);

	const panel = document.createElement('div');
	panel.className = 'kardex-summary-panel';
	panel.appendChild(heading);
	panel.appendChild(table);

	if (Number(data.line_count) === 0) {
		const hint = document.createElement('p');
		hint.className = 'mts muted';
		hint.innerHTML =
			'Nenhuma transação no período para esta empresa e produto. Confira se a <strong>Empresa</strong> é a mesma da lista em <strong>Transações</strong>.';
		panel.appendChild(hint);
	}

	el.appendChild(panel);
}

function getSelectedProductId(work) {
	const sel = $('#kx-product', work);
	return sel?.value?.trim() || '';
}

function downloadKardexCsv(base64Content, filename) {
	const bin = atob(base64Content);
	const arr = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
	const blob = new Blob([arr], { type: 'text/csv;charset=utf-8' });
	const a = document.createElement('a');
	a.href = URL.createObjectURL(blob);
	a.download = filename || 'kardex-analitico.csv';
	a.click();
	URL.revokeObjectURL(a.href);
}

/** Corpo comum para impressão Kardex (PDF/CSV). */
function kardexPrintBody(work) {
	const productId = getSelectedProductId(work);
	const df = $('#kx-df', work).value;
	const dt = $('#kx-dt', work).value;
	const lotEl = $('#kx-lot', work);
	const body = {
		product_id: Number(productId),
		date_from: df,
		date_to: dt,
		cost_mode: $('#kx-cost', work).value,
	};
	if (lotEl?.value) body.lot_id = Number(lotEl.value);
	if ($('#kx-dis', work).checked) body.include_disabled = true;
	return body;
}

export default async function bi_kardex() {
	const work = document.getElementById('workarea');
	work.innerHTML = `
		<style>
			.kardex-form { max-width: 56rem; }
			.kardex-form label { display: block; margin-top: 0.75rem; }
			.kardex-form .row { display: flex; flex-wrap: wrap; gap: 1rem; align-items: flex-end; }
			.kardex-form .row > label { flex: 1 1 12rem; }
			.kardex-prod-wrap { flex: 1 1 100%; }
			.kardex-prod-wrap label { margin-top: 0.5rem; }
			.kx-product-select {
				width: 100%;
				min-height: 14rem;
				margin-top: 0.35rem;
				font-size: 0.9rem;
			}
			.kardex-actions { margin-top: 1rem; display: flex; gap: 0.75rem; flex-wrap: wrap; align-items: center; }
			.kardex-actions .kx-print-group { display: inline-flex; gap: 0.35rem; flex-wrap: wrap; align-items: center; margin-left: 0.25rem; padding-left: 0.75rem; border-left: 1px solid var(--color-border, #ccc); }
			.kardex-scroll { overflow: auto; max-height: 70vh; margin-top: 1rem; }
			/* Tabelas .kardex-table / .kardex-summary-table: ver css/main.css (#workarea) */
			.tar { text-align: right; }
			.muted { color: #555; font-size: 0.9rem; }
			.kardex-json { margin-top: 1rem; font-size: 0.8rem; overflow: auto; max-height: 16rem; background: #f8f8f8; padding: 0.5rem; }
			.kardex-summary-panel {
				background: var(--color-box, #fff);
				border: 1px solid var(--color-border, #ccc);
				border-radius: 4px;
				padding: 0.75rem 1rem;
				max-width: 40rem;
			}
			.kardex-summary-heading { font-size: 1.05rem; margin: 0 0 0.5rem 0; font-weight: 600; color: var(--color-text, #111); }
		</style>
		<div class="form kardex-form">
			<div class="title">Kardex (BI)</div>
			<p class="mts muted">Saldo por <strong>empresa</strong> e <strong>produto</strong> — a empresa deve ser a <strong>mesma</strong> da coluna <strong>Empresa</strong> nas <strong>Transações</strong> (cada empresa tem seu <code>company_id</code> e seus movimentos). A lista de produtos carrega <strong>todos</strong> os itens do cadastro (várias requisições de até 500), incluindo <strong>inativos</strong>, para o Kardex não omitir SKU com movimento. Use o filtro por SKU/nome. A API usa <code>product_id</code> no contexto dessa empresa.</p>
			<div class="row">
				<label>Empresa<sup>*</sup><select id="kx-company"></select></label>
				<label>Lote (opcional)<input type="number" id="kx-lot" min="1" step="1" placeholder="vazio = todos"></label>
			</div>
			<div class="kardex-prod-wrap">
				<label>Filtrar lista (SKU ou nome)<input type="search" id="kx-prod-search" placeholder="Digite para reduzir opções…" autocomplete="off"></label>
				<label>Produto<sup>*</sup>
					<select id="kx-product" class="kx-product-select" size="12"></select>
				</label>
			</div>
			<div class="row">
				<label>Data inicial<sup>*</sup><input type="date" id="kx-df"></label>
				<label>Data final<sup>*</sup><input type="date" id="kx-dt"></label>
				<label>Modo custo
					<select id="kx-cost">
						<option value="none">Sem valor (só quantidades)</option>
						<option value="transaction">Valores da transação (sinalizado)</option>
					</select>
				</label>
			</div>
			<div class="row">
				<label>Limite<input type="number" id="kx-limit" value="500" min="1" max="5000" step="1"></label>
				<label>Offset<input type="number" id="kx-offset" value="0" min="0" step="1"></label>
				<label><input type="checkbox" id="kx-dis"> Incluir transações desabilitadas (estorno)</label>
			</div>
			<div class="kardex-actions">
				<button type="button" class="button" id="kx-run-detail">Gerar Kardex (analítico)</button>
				<button type="button" class="button" id="kx-run-summary">Resumo do período</button>
				<span class="kx-print-group">
					<span class="muted" style="font-size:0.85rem">Relatório:</span>
					<button type="button" class="button" id="kx-print-pdf">PDF</button>
					<button type="button" class="button" id="kx-print-csv">CSV</button>
				</span>
			</div>
		</div>
		<div id="kx-summary-out" class="mts"></div>
		<div id="kx-detail-out" class="mts"></div>
		<details class="mts"><summary>JSON bruto (última resposta)</summary><pre id="kx-raw" class="kardex-json"></pre></details>
	`;

	const selCo = $('#kx-company', work);
	const selProd = $('#kx-product', work);
	const inpSearch = $('#kx-prod-search', work);

	await loadCompanies(selCo);
	selProd.innerHTML = '<option value="">— Escolha a empresa —</option>';

	selCo.addEventListener('change', async () => {
		await loadProductsForCompany(selCo.value, selProd, inpSearch);
	});

	inpSearch.addEventListener('input', () => applyProductSearch(selProd, inpSearch.value));

	selProd.addEventListener('change', () => applyProductSearch(selProd, inpSearch.value));

	$('#kx-run-summary', work).addEventListener('click', async () => {
		try {
			const companyId = selCo.value;
			const productId = getSelectedProductId(work);
			if (!companyId || !productId) {
				notify('Selecione empresa e produto. Se você filtrou a lista e o produto “sumiu”, limpe o filtro ou clique de novo no produto.');
				return;
			}
			const df = $('#kx-df', work).value;
			const dt = $('#kx-dt', work).value;
			if (!df || !dt) {
				notify('Informe data inicial e final.');
				return;
			}
			const cost = $('#kx-cost', work).value;
			const q = buildQuery({
				product_id: productId,
				date_from: df,
				date_to: dt,
				lot_id: $('#kx-lot', work).value || undefined,
				cost_mode: cost,
				include_disabled: $('#kx-dis', work).checked ? '1' : undefined,
			});
			const res = await api.kardex_summary(companyId, q);
			const raw = (await res.text()).replace(/^\uFEFF/, '');
			const rawEl = $('#kx-raw', work);
			if (rawEl) rawEl.textContent = raw;
			if (!res.ok) {
				notify(raw || res.statusText, 'Kardex resumo');
				return;
			}
			let data;
			try {
				data = JSON.parse(raw);
			} catch (e) {
				notify('Resposta do resumo não é JSON válido.', 'Kardex resumo');
				return;
			}
			const outSum = $('#kx-summary-out', work);
			if (!outSum) {
				notify('Área de resultado não encontrada na página.', 'Kardex resumo');
				return;
			}
			renderSummary(outSum, data, cost === 'transaction');
			const outDet = $('#kx-detail-out', work);
			if (outDet) outDet.innerHTML = '';
		} catch (e) {
			console.error('Kardex resumo:', e);
			notify(e?.message || String(e), 'Kardex resumo');
		}
	});

	$('#kx-run-detail', work).addEventListener('click', async () => {
		const companyId = selCo.value;
		const productId = getSelectedProductId(work);
		if (!companyId || !productId) {
			notify('Selecione empresa e um produto na lista.');
			return;
		}
		const df = $('#kx-df', work).value;
		const dt = $('#kx-dt', work).value;
		if (!df || !dt) {
			notify('Informe data inicial e final.');
			return;
		}
		const cost = $('#kx-cost', work).value;
		const q = buildQuery({
			product_id: productId,
			date_from: df,
			date_to: dt,
			lot_id: $('#kx-lot', work).value || undefined,
			cost_mode: cost,
			include_disabled: $('#kx-dis', work).checked ? '1' : undefined,
			limit: $('#kx-limit', work).value || '500',
			offset: $('#kx-offset', work).value || '0',
		});
		const res = await api.kardex_report(companyId, q);
		const raw = await res.text();
		$('#kx-raw', work).textContent = raw;
		if (!res.ok) {
			notify(raw || res.statusText, 'Kardex');
			return;
		}
		const data = JSON.parse(raw);
		await prefetchKardexLookups(companyId, productId);
		await renderDetail($('#kx-detail-out', work), data, cost === 'transaction', companyId);
		const sumOut = $('#kx-summary-out', work);
		if (sumOut) sumOut.replaceChildren();
	});

	const runPrint = async (format) => {
		const companyId = selCo.value;
		const productId = getSelectedProductId(work);
		if (!companyId || !productId) {
			notify('Selecione empresa e um produto na lista.');
			return;
		}
		const df = $('#kx-df', work).value;
		const dt = $('#kx-dt', work).value;
		if (!df || !dt) {
			notify('Informe data inicial e final.');
			return;
		}
		const payload = { ...kardexPrintBody(work), format };
		const json = await api.call(api.print_kardex, companyId, payload);
		if (!json) return;
		if (json.pdf) {
			pdf(json.pdf);
			return;
		}
		if (json.csv) {
			downloadKardexCsv(json.csv, json.filename);
			notify('Download do CSV iniciado.', 'Kardex');
			return;
		}
		notify(json.message || 'Nenhum arquivo retornado.', 'Kardex');
	};

	$('#kx-print-pdf', work).addEventListener('click', () => runPrint('pdf'));
	$('#kx-print-csv', work).addEventListener('click', () => runPrint('csv'));
}
